/*
 * WHAT A RUN TELLS THE MODEL — and the same text read back apart, for the person.
 *
 * A run reaches the model as ONE plain-text message: what the datasets hold, what the code printed,
 * the value of its last expression, its warnings, the files it wrote — or the traceback. The
 * transcript card shows those as separate things (a printed table as a table, a traceback as an
 * error), so it needs them apart again.
 *
 * WHY THE MESSAGE IS READ BACK RATHER THAN THE PARTS COPIED INTO `data`. `data` is not hidden from
 * the model: every path that builds its context — the server's (`rowsToWire`), the archive's
 * (`archiveToWire`), the tab's own mirror — sends `message` followed by `JSON.stringify(data)`. A
 * second copy of stdout there would be read twice on every later turn, and would push more runs
 * over the archive's inline cap, past which a result is kept only as its start. So the message
 * stays the single record, and the card reads it.
 *
 * THE MARKERS LIVE HERE, used in both directions — `sandbox-tools.ts` builds the message from them
 * and `readOutcome` takes it apart by them — so the two cannot drift: changing one sentence changes
 * both, and test/sandbox-outcome.test.ts builds messages through the real tool and reads them back.
 */

/** Between the parts of a message. */
export const GAP = '\n\n';

/**
 * TOLD ONCE, when the pool took this conversation's interpreter since its last
 * run (see `takeRestartNotice`). The prompt says state carries between calls, and
 * the model builds on it; an empty interpreter it was not warned about reads as a
 * bug in its own code.
 */
export const RESTARTED =
  "This conversation's Python interpreter was restarted since your last run (it had been idle, or another " +
  'conversation needed the room), so nothing an earlier run defined is still there: this run started with only ' +
  'its own datasets. Recompute what you need rather than assuming it exists.';

export const LAST_EXPRESSION = '(last expression) ';
export const STDERR = 'stderr:\n';
export const PRINTED_NOTHING = 'It ran and printed nothing. print() what you want to see.';
export const RAISED = 'The code raised:\n';
export const PRINTED_FIRST = `${GAP}It printed this first:\n`;
/** What every timeout says, whichever clock ran out — how a stop is told from other failures. */
export const RESET = 'so the interpreter for this conversation was reset.';
export const PRINTED_BEFORE_STOP = ' It printed this before it was stopped:\n';

/** The first line `describeDatasets` writes, in all its forms — over files only it names no zone. */
const DATASETS_HEAD = /^Loaded \d+ datasets?(?: over | \(times UTC\):|:)/;
/** One file the run wrote: "produced chart.png (image/png, 41203 bytes)". */
const PRODUCED = /^produced .+ \([^()]+, \d+ bytes\)$/;
/** What the archive appends to a failure it kept only the start of. */
const CUT_ERROR = /\n… \(truncated from \d+ chars\)$/;

export interface RunReport {
  /** The interpreter had been restarted before this run. */
  restarted: boolean;
  /** The datasets paragraph the model read; '' when the run loaded none. */
  datasets: string;
  /** What the code printed — on a failure, what it printed before it failed or was stopped. */
  stdout: string;
  /** The last expression's value; '' when there was none. */
  result: string;
  /** Warnings on a run that succeeded. */
  stderr: string;
  /** One line per file the run wrote. */
  produced: string[];
  /**
   * Why it did not succeed: `raised` carries the traceback, `stopped` the sentence saying which
   * clock ran out, `refused` the whole message — a run that never reached the interpreter (no code,
   * a dataset that would not load, a sandbox that would not start).
   */
  failure?: { kind: 'raised' | 'stopped' | 'refused'; text: string };
  /** The archive kept only the start of this result; what is here is that start. */
  cut: boolean;
}

/**
 * Take a run's message apart.
 *
 * `loaded` is how many datasets the call named: the datasets paragraph is only looked for when
 * there is one, so a run with none whose output happens to begin "Loaded 3 datasets" keeps it.
 * `cut` says the archive kept only the start of a success (it then has no `data`); the parts at the
 * end are gone, so everything after the head is shown as output.
 */
export function readOutcome(message: string, ok: boolean, loaded: number, cut = false): RunReport {
  const report: RunReport = { restarted: false, datasets: '', stdout: '', result: '', stderr: '', produced: [], cut };
  let body = message;
  if (!ok && CUT_ERROR.test(body)) {
    body = body.replace(CUT_ERROR, '');
    report.cut = true;
  }
  if (body.startsWith(RESTARTED + GAP)) {
    report.restarted = true;
    body = body.slice(RESTARTED.length + GAP.length);
  }
  // The paragraph has single line breaks inside it and a gap after it.
  if (loaded > 0 && DATASETS_HEAD.test(body)) {
    const at = body.indexOf(GAP);
    report.datasets = at < 0 ? body : body.slice(0, at);
    body = at < 0 ? '' : body.slice(at + GAP.length);
  }

  if (!ok) {
    if (body.startsWith(RAISED)) {
      const rest = body.slice(RAISED.length);
      const at = rest.indexOf(PRINTED_FIRST);
      report.failure = { kind: 'raised', text: at < 0 ? rest : rest.slice(0, at) };
      report.stdout = at < 0 ? '' : rest.slice(at + PRINTED_FIRST.length);
    } else if (body.includes(RESET)) {
      const at = body.indexOf(PRINTED_BEFORE_STOP);
      report.failure = { kind: 'stopped', text: at < 0 ? body : body.slice(0, at) };
      report.stdout = at < 0 ? '' : body.slice(at + PRINTED_BEFORE_STOP.length);
    } else {
      report.failure = { kind: 'refused', text: body };
    }
    return report;
  }

  if (cut || body === PRINTED_NOTHING) {
    report.stdout = cut ? body : '';
    return report;
  }
  // From the END, because that is where the parts with fixed shapes are; stdout, which can say
  // anything, is whatever is left at the front.
  for (;;) {
    const at = body.lastIndexOf(GAP);
    const tail = at < 0 ? body : body.slice(at + GAP.length);
    if (!tail || !PRODUCED.test(tail)) break;
    report.produced.unshift(tail);
    body = at < 0 ? '' : body.slice(0, at);
  }
  // The last occurrence after a gap; at the very start only when there is nothing before it.
  const take = (marker: string): string => {
    let at = body.lastIndexOf(GAP + marker);
    let from = at + GAP.length + marker.length;
    if (at < 0) {
      if (!body.startsWith(marker)) return '';
      at = 0;
      from = marker.length;
    }
    const text = body.slice(from);
    body = body.slice(0, at);
    return text;
  };
  report.stderr = take(STDERR);
  report.result = take(LAST_EXPRESSION);
  report.stdout = body;
  return report;
}
